import { PRESCRIPTIONS_ROUTE } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { useControlledDialogProps } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import { ErrorMessage } from '@montugroup/circuit-shared/components/common/ErrorMessage';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  toast,
} from '@montugroup/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

const logger = new Logger('DeletePrescriptionModal');
const client = getTsCustomerPortalClient();
const successMessage = 'Successfully deleted prescription';
const errorMessage = 'Failed to deleted prescription';

export function DeletePrescriptionModal({
  open,
  onClose,
  prescriptionId,
}: {
  open: boolean;
  onClose: () => void;
  prescriptionId: number;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate, error, isPending, contractEndpoint, reset } = client.prescription.deletePrescription.useMutation({
    onSuccess: () => {
      toast.success(successMessage);
      logger.info(successMessage, { prescriptionId });
      queryClient.invalidateQueries({ queryKey: client.prescription.listPrescriptions.keys });
      controlledDialogProps.onClose();
      navigate(PRESCRIPTIONS_ROUTE);
      queryClient.removeQueries({ queryKey: [...client.prescription.getPrescription.keys, prescriptionId] });
    },
    onError: (e) => {
      toast.error(errorMessage);
      logger.error(errorMessage, e, { prescriptionId });
    },
  });

  const controlledDialogProps = useControlledDialogProps({
    open,
    onClose: () => {
      onClose();
      reset();
    },
  });

  return (
    <Dialog {...controlledDialogProps} fullWidth>
      <DialogTitle>Delete prescription</DialogTitle>
      <DialogContent>
        <DialogContentText>Are you sure you want to delete this prescription from uMeds?</DialogContentText>
      </DialogContent>
      <ErrorMessage error={error} contractEndpoint={contractEndpoint} />
      <DialogActions>
        <Button
          variant="text"
          disabled={!controlledDialogProps.open || isPending}
          onClick={controlledDialogProps.onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          loading={isPending}
          onClick={() => mutate({ params: { id: prescriptionId } })}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
